import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { SOCKET_EVENTS } from '@maios/shared';
import { getChannel, getPinnedMessages, unarchiveChannel } from '../services/chatChannelService';
import { getSocket } from '../lib/socket';
import type { ChatMessage } from '../services/chatService';
import { ChannelChatProvider, useChannelChat } from '../contexts/ChannelChatContext';
import ChatChannelHeader from '../components/chat/ChatChannelHeader';
import ChatSearchPanel from '../components/chat/ChatSearchPanel';
import ChatMemberDrawer from '../components/chat/ChatMemberDrawer';
import ChatMessageList from '../components/chat/ChatMessageList';
import ChatInput from '../components/chat/ChatInput';
import TypingIndicator from '../components/chat/TypingIndicator';
import ThreadPanel from '../components/chat/ThreadPanel';
import PinnedMessageBanner from '../components/chat/PinnedMessageBanner';
import PinnedMessageList from '../components/chat/PinnedMessageList';
import Spinner from '../components/ui/Spinner';
import { useTypingIndicator } from '../hooks/useTypingIndicator';
import { useAuthStore } from '../store/authStore';

export default function ChatChannelPage() {
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: channel, isLoading, error } = useQuery({
    queryKey: ['chatChannel', channelId],
    queryFn: () => getChannel(channelId!),
    enabled: !!channelId,
    retry: (count, err) => {
      if ((err as { response?: { status?: number } })?.response?.status === 403) return false;
      return count < 2;
    },
  });

  // Listen for kick event
  useEffect(() => {
    const socket = getSocket();
    if (!socket || !channelId) return;
    const handleKicked = (data: { channelId: string; channelName: string }) => {
      if (data.channelId === channelId) {
        toast.error(`'${data.channelName}' 채널에서 추방되었습니다.`);
        queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
        navigate('/chat', { replace: true });
      }
    };
    socket.on(SOCKET_EVENTS.CHANNEL_KICKED, handleKicked);
    return () => { socket.off(SOCKET_EVENTS.CHANNEL_KICKED, handleKicked); };
  }, [channelId, navigate, queryClient]);

  if (isLoading) {
    return <div className="flex items-center justify-center h-full"><Spinner className="size-6" /></div>;
  }

  // 403 or missing channel
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 403) {
    return <div className="flex items-center justify-center h-full"><p className="text-sm text-gray-400">접근 권한이 없습니다.</p></div>;
  }
  if (!channel) {
    return <div className="flex items-center justify-center h-full"><p className="text-sm text-gray-400">채널을 찾을 수 없습니다.</p></div>;
  }

  return (
    <ChannelChatProvider channelId={channel.id}>
      <ChannelChatView channel={channel} />
    </ChannelChatProvider>
  );
}

function ChannelChatView({ channel }: { channel: ReturnType<typeof getChannel> extends Promise<infer T> ? T : never }) {
  const { messages, isLoading, loadingMore, hasMore, sending, sendMessage, loadMore, onNewMessage } = useChannelChat();
  const userId = useAuthStore((s) => s.user?.id);
  const queryClient = useQueryClient();

  const [showMembers, setShowMembers] = useState(false);
  const { typingUsers, handleTyping, stopTyping } = useTypingIndicator(channel.id);
  const [showSearch, setShowSearch] = useState(false);
  const [highlightedMsgId, setHighlightedMsgId] = useState<string | null>(null);
  const [showNewMsgBtn, setShowNewMsgBtn] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [showPinnedList, setShowPinnedList] = useState(false);

  // Pinned messages count
  const { data: pinnedData } = useQuery({
    queryKey: ['pinnedMessages', channel.id],
    queryFn: () => getPinnedMessages(channel.id, 1, 1),
    staleTime: 30_000,
  });
  const pinnedCount = pinnedData?.pagination?.total ?? 0;

  const invalidatePins = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['pinnedMessages', channel.id] });
  }, [queryClient, channel.id]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  // Auto-scroll on new message
  useEffect(() => {
    const unsub = onNewMessage((_msg, isMine) => {
      if (isMine || isAtBottomRef.current) {
        requestAnimationFrame(() => {
          endRef.current?.scrollIntoView({ behavior: 'smooth' });
        });
      } else {
        setShowNewMsgBtn(true);
      }
      // Refresh channel list unread counts
      queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
    });
    return unsub;
  }, [onNewMessage, queryClient]);

  // Pin/unpin socket listener → invalidate pin queries
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const handler = () => invalidatePins();
    socket.on(SOCKET_EVENTS.CHANNEL_MESSAGE_PINNED, handler);
    socket.on(SOCKET_EVENTS.CHANNEL_MESSAGE_UNPINNED, handler);
    return () => {
      socket.off(SOCKET_EVENTS.CHANNEL_MESSAGE_PINNED, handler);
      socket.off(SOCKET_EVENTS.CHANNEL_MESSAGE_UNPINNED, handler);
    };
  }, [invalidatePins]);

  // Archive/unarchive socket listener → invalidate channel data
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['chatChannel', channel.id] });
    };
    socket.on(SOCKET_EVENTS.CHANNEL_ARCHIVED, handler);
    socket.on(SOCKET_EVENTS.CHANNEL_UNARCHIVED, handler);
    return () => {
      socket.off(SOCKET_EVENTS.CHANNEL_ARCHIVED, handler);
      socket.off(SOCKET_EVENTS.CHANNEL_UNARCHIVED, handler);
    };
  }, [channel.id, queryClient]);

  // Initial scroll to bottom
  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      endRef.current?.scrollIntoView();
      requestAnimationFrame(() => {
        endRef.current?.scrollIntoView();
      });
      const timer = setTimeout(() => {
        endRef.current?.scrollIntoView();
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isLoading, channel.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScrollChange = useCallback((atBottom: boolean) => {
    isAtBottomRef.current = atBottom;
    if (atBottom) setShowNewMsgBtn(false);
  }, []);

  const scrollToBottom = useCallback(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowNewMsgBtn(false);
  }, []);

  const handleJumpToMessage = useCallback((messageId: string) => {
    setHighlightedMsgId(messageId);
    setShowSearch(false);
    // Try to scroll to the message if already loaded
    requestAnimationFrame(() => {
      const el = document.getElementById(`msg-${messageId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Clear highlight after 2 seconds
        setTimeout(() => setHighlightedMsgId(null), 2500);
      } else {
        // Message not loaded — just clear highlight
        setTimeout(() => setHighlightedMsgId(null), 2500);
      }
    });
  }, []);

  const handleSend = useCallback(async (content: string, attachments?: unknown[], mentions?: unknown[]) => {
    await sendMessage(content, attachments, mentions);
    queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
  }, [sendMessage, queryClient]);

  // CHG-123 REQ-V123-042: ?messageId 도착 시 자동 스크롤 + 하이라이트
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const targetMsgId = searchParams.get('messageId');
    if (!targetMsgId) return;
    const exists = messages.some((m) => m.messageId === targetMsgId);
    if (exists) {
      handleJumpToMessage(targetMsgId);
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('messageId'); return next; }, { replace: true });
    } else if (messages.length > 0 && !isLoading) {
      // 페이지네이션 다음 페이지에 있을 수 있음 — 현재는 graceful fallback
      toast('원본 메시지를 찾을 수 없습니다. 이전 페이지에 있을 수 있습니다.');
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('messageId'); return next; }, { replace: true });
    }
  }, [searchParams, messages, handleJumpToMessage, setSearchParams, isLoading]);

  // Map ChatMessageItem to ChatMessage for compatibility with ChatMessageList
  const mappedMessages = messages.map((m) => ({
    messageId: m.messageId,
    content: m.content,
    mentions: (m.mentions ?? []) as ChatMessage['mentions'],
    resolvedMentions: m.resolvedMentions as ChatMessage['resolvedMentions'],
    reactions: m.reactions ?? [],
    attachments: m.attachments ?? [],
    isEdited: m.isEdited ?? false,
    isDeleted: m.isDeleted ?? false,
    editedAt: m.editedAt ?? null,
    createdAt: m.createdAt,
    sender: m.sender,
    // CHG-075-P4: 스레드 정보
    threadCount: m.threadCount ?? 0,
    lastThreadAt: m.lastThreadAt ?? null,
    threadPreview: m.threadPreview ?? [],
    isPinned: m.isPinned ?? false,
    // CHG-123 REQ-V123-050: linkedResources 전달 (배지 렌더링용)
    linkedResources: m.linkedResources,
  })) as ChatMessage[];

  // Project ID for mentions (only for PROJECT channels)
  const projectId = channel.type === 'PROJECT' && channel.project ? channel.project.id : undefined;

  return (
    <div className="flex h-full">
      <div className="flex-1 flex flex-col min-w-0">
        <ChatChannelHeader channel={channel}
          onShowMembers={() => setShowMembers((v) => !v)}
          onToggleSearch={() => setShowSearch((v) => !v)}
          pinnedCount={pinnedCount}
          onTogglePinnedList={() => setShowPinnedList((v) => !v)} />

        {showSearch && (
          <ChatSearchPanel channelId={channel.id}
            onClose={() => setShowSearch(false)}
            onJumpToMessage={handleJumpToMessage} />
        )}

        {/* Pinned message banner + dropdown list */}
        <div className="relative shrink-0">
          {pinnedCount > 0 && (
            <PinnedMessageBanner channelId={channel.id}
              onToggleList={() => setShowPinnedList((v) => !v)}
              onJumpToMessage={handleJumpToMessage} />
          )}
          <PinnedMessageList channelId={channel.id}
            isOpen={showPinnedList}
            onClose={() => setShowPinnedList(false)}
            onJumpToMessage={handleJumpToMessage}
            currentUserRole={channel.myRole}
            currentUserId={userId} />
        </div>

        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          <ChatMessageList
            messages={mappedMessages}
            isLoading={isLoading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            onLoadMore={loadMore}
            currentUserId={userId}
            showNewMsgBtn={showNewMsgBtn}
            onNewMsgBtnClick={scrollToBottom}
            scrollContainerRef={scrollRef as React.RefObject<HTMLDivElement>}
            messagesEndRef={endRef as React.RefObject<HTMLDivElement>}
            onScrollChange={handleScrollChange}
            highlightedMessageId={highlightedMsgId}
            channelId={channel.id}
            onOpenThread={setActiveThreadId}
            currentUserRole={channel.myRole}
            onPinChange={invalidatePins}
            isArchived={channel.isArchived}
            channelType={channel.type}
            contextProjectId={channel.type === 'PROJECT' && channel.project ? channel.project.id : undefined}
            contextProjectKey={channel.type === 'PROJECT' && channel.project ? channel.project.projectKey : undefined}
          />
        </div>

        {channel.isArchived ? (
          <div className="flex items-center justify-between px-4 py-3 bg-yellow-50 border-t shrink-0">
            <div className="flex items-center gap-2 text-sm text-yellow-700">
              <span>📦</span>
              <span>이 채널은 아카이브 되었습니다.</span>
            </div>
            {(channel.myRole === 'OWNER' || (channel.type === 'PROJECT' && channel.myRole)) && (
              <button onClick={async () => {
                try {
                  await unarchiveChannel(channel.id);
                  queryClient.invalidateQueries({ queryKey: ['chatChannel', channel.id] });
                  queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
                  toast.success('아카이브가 해제되었습니다.');
                } catch { toast.error('아카이브 해제에 실패했습니다.'); }
              }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                아카이브 해제
              </button>
            )}
          </div>
        ) : (
          <>
            <TypingIndicator typingUsers={typingUsers} />
            <ChatInput
              projectId={projectId ?? ''}
              channelId={channel.id}
              onSend={handleSend}
              sending={sending}
              disabled={!channel.myRole}
              onTyping={handleTyping}
              onStopTyping={stopTyping}
              channelMembers={channel.members?.map((m) => ({ userId: m.userId, name: m.name, avatarUrl: m.avatarUrl }))}
              currentUserRole={channel.myRole}
              currentUserId={userId}
            />
          </>
        )}
      </div>

      {/* Thread panel */}
      {activeThreadId && (
        <ThreadPanel
          channelId={channel.id}
          parentMessageId={activeThreadId}
          projectId={projectId}
          onClose={() => setActiveThreadId(null)}
          isArchived={channel.isArchived}
          channelType={channel.type}
          contextProjectKey={channel.type === 'PROJECT' && channel.project ? channel.project.projectKey : undefined}
        />
      )}

      {showMembers && (
        <ChatMemberDrawer channel={channel} onClose={() => setShowMembers(false)} />
      )}
    </div>
  );
}
